import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, AlertTriangle, TrendingUp, CheckCircle2, Calendar, Sparkles, TrendingDown, ShieldAlert, Banknote, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { baseURL } from '@/config/APIConfig';
import { getTodayForInput, getMonthsAgoForInput } from '../../utils/dateUtils';

const API = baseURL.dashboardApi;

// Map icons based on category and priority
const getIconComponent = (category, priority) => {
    const iconMap = {
        'Performance': TrendingUp,
        'Rate': TrendingUp,
        'Booking': CheckCircle2,
        'Compliance': ShieldAlert,
        'Financial': Banknote,
        'Risk': AlertTriangle,
        'Opportunity': Target,
        'high': AlertTriangle,
        'medium': TrendingUp,
        'low': CheckCircle2,
    };
    const Icon = iconMap[category] || iconMap[priority] || Brain;
    return Icon;
};

// Map accent colors based on category
const getAccentMap = (category, priority) => {
    const accentColors = {
        'Performance': { bar: 'bg-emerald-500', icon: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', cat: 'text-emerald-600 dark:text-emerald-400' },
        'Rate': { bar: 'bg-blue-500', icon: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', cat: 'text-blue-600 dark:text-blue-400' },
        'Booking': { bar: 'bg-violet-500', icon: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400', cat: 'text-violet-600 dark:text-violet-400' },
        'Compliance': { bar: 'bg-amber-500', icon: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', cat: 'text-amber-600 dark:text-amber-400' },
        'Financial': { bar: 'bg-emerald-500', icon: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', cat: 'text-emerald-600 dark:text-emerald-400' },
        'Risk': { bar: 'bg-rose-500', icon: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400', cat: 'text-rose-600 dark:text-rose-400' },
        'Opportunity': { bar: 'bg-teal-500', icon: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400', cat: 'text-teal-600 dark:text-teal-400' },
    };
    return accentColors[category] || accentColors['Performance'];
};

// Priority pill styling
const getPriorityPill = (priority) => {
    const priorityStyles = {
        'high': 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800',
        'medium': 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
        'low': 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800',
    };
    return priorityStyles[priority] || priorityStyles['low'];
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
};

const AIInsightsPanel = ({ fullPage = false }) => {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(getMonthsAgoForInput(3));
    const [toDate, setToDate] = useState(getTodayForInput());

    useEffect(() => {
        fetchInsights();
    }, []);

    const fetchInsights = async () => {
        try {
            const response = await axios.get(`${API}/ai/insights`);
            // Transform data if needed to match expected format
            const transformedData = response.data.map(item => ({
                ...item,
                title: item.title || item.headline,
                body: item.content || item.description,
                cat: item.category,
                priority: item.priority?.toLowerCase() || 'medium'
            }));
            setInsights(transformedData);
        } catch (error) {
            console.error('Error fetching AI insights:', error);
            // Fallback mock data for demo
            setInsights([
                { id: 1, cat: 'Performance', title: 'Lane Performance Alert', body: 'Shanghai → Los Angeles route shows 15% improvement in transit times this month.', priority: 'high' },
                { id: 2, cat: 'Rate', title: 'Rate Optimization Opportunity', body: 'Spot rates on Rotterdam → New York decreased by 8%. Consider rebooking.', priority: 'medium' },
                { id: 3, cat: 'Booking', title: 'Booking Trend Detected', body: 'Increased volume on Singapore → London route. Secure capacity early.', priority: 'low' },
                { id: 4, cat: 'Compliance', title: 'Documentation Alert', body: '3 shipments missing customs docs. Review now to avoid delays.', priority: 'high' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 animate-pulse" />
                        <div>
                            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                            <div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded mt-1 animate-pulse" />
                        </div>
                    </div>
                </div>
                <div className="p-5 space-y-3">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-24 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
                    ))}
                </div>
            </section>
        );
    }

    if (fullPage) {
        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
                    <div className="flex items-center space-x-3 mb-4">
                        <Sparkles className="w-8 h-8" />
                        <h2 className="text-3xl font-bold">AI-Powered Insights</h2>
                    </div>
                    <p className="text-white/90 text-lg">
                        Real-time intelligence and predictive analytics for your logistics operations
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {insights.slice(0, 3).map((insight, idx) => {
                        const Icon = getIconComponent(insight.cat, insight.priority);
                        const accent = getAccentMap(insight.cat, insight.priority);
                        return (
                            <div key={idx} className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden hover:shadow-lg transition-all">
                                <div className="p-6">
                                    <div className={`w-12 h-12 rounded-xl ${accent.icon} flex items-center justify-center mb-4`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${accent.cat} mb-2`}>
                                        {insight.cat}
                                    </div>
                                    <div className="text-[14px] font-semibold text-slate-900 dark:text-white mb-2">{insight.title}</div>
                                    <div className="text-[12px] text-slate-600 dark:text-slate-400 leading-relaxed">{insight.body}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
            data-testid="ai-insights-panel"
        >
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 text-white flex items-center justify-center shadow-[0_8px_18px_-6px_rgba(139,92,246,0.5)]">
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[14px] font-semibold text-slate-900 dark:text-white">AI Insights</div>
                        <div className="text-[12px] text-slate-500 dark:text-slate-400">Smart recommendations</div>
                    </div>
                </div>
                <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-[12px] text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900">
                    <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                    From {formatDate(fromDate)} — To {formatDate(toDate)}
                </div>
            </div>

            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {insights.map((it, i) => {
                    const Icon = getIconComponent(it.cat, it.priority);
                    const accent = getAccentMap(it.cat, it.priority);
                    const priorityPill = getPriorityPill(it.priority);
                    
                    return (
                        <div
                            key={i}
                            className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900
                                     hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-4"
                        >
                            <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent.bar}`} />
                            <div className="flex items-start justify-between gap-3 ml-2">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent.icon}`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${accent.cat}`}>
                                            {it.cat || it.category}
                                        </div>
                                        <div className="text-[14px] font-semibold text-slate-900 dark:text-white mt-0.5">
                                            {it.title || it.headline}
                                        </div>
                                        <div className="text-[12px] text-slate-600 dark:text-slate-400 mt-1.5 leading-relaxed">
                                            {it.body || it.content || it.description}
                                        </div>
                                    </div>
                                </div>
                                <span className={`shrink-0 inline-flex items-center px-2 h-5 rounded-md text-[10px] font-bold tracking-wider ${priorityPill}`}>
                                    {(it.priority || 'medium').toUpperCase()}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </motion.section>
    );
};

export default AIInsightsPanel;